import React, {useEffect, useState} from 'react';
import axios from 'axios';
import Form from 'react-bootstrap/Form';
import AsyncSelect from 'react-select/async';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import ActionAssignBaseFormFields from './ActionAssignBaseFormFields';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {isFieldRestricted} from '@/utils/fieldRestrictions';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';

interface Agent {
    id: number;
    name: string;
}

interface AgentOption {
    label: string;
    value: number;
}

interface AgentResponse {
    data: {
        items: Agent[];
    };
}

interface FormValues {
    params: {
        agentId: number;
        title?: string;
        description?: string;
        timeout?: string;
    };
}

interface FormikProps {
    values: FormValues;
    setFieldValue: (field: string, value: any) => void;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (e: React.FocusEvent) => void;
    touched: {
        params?: {
            agentId?: boolean;
            title?: boolean;
            timeout?: boolean;
        };
    };
    errors: {
        params?: {
            agentId?: string;
            title?: string;
            timeout?: string;
        };
    };
}

interface ActionAssignAgentFormFieldsProps {
    formik: FormikProps;
    setTimeoutValidity: (valid: boolean) => void;
}

const loadAgentsRequest = (inputValue: string): Promise<AgentResponse> => axios.get(window.URLS.agentsResource, {
    params: {
        query: inputValue,
    },
})
    .catch((error) => {
        if (error.response.status === 400) {
            return error.response;
        }

        throw error;
    });

const loadAgentsRequestDebounced = AwesomeDebouncePromise(loadAgentsRequest, 280);

const ActionAssignAgentFormFields: React.FC<ActionAssignAgentFormFieldsProps> = React.memo((
    {
        formik,
        setTimeoutValidity,
    },
) => {
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const agentRestricted = isFieldRestricted('agent_pii', restrictedFieldKeys);

    const getAgentOptions = async (inputValue: string): Promise<AgentOption[]> => {
        const results = await loadAgentsRequestDebounced(inputValue);
        return results.data.items.map((agent) => ({
            label: agent.name || `Agent #${agent.id}`,
            value: agent.id,
        }));
    };

    const [agentName, setAgentName] = useState<string>('');

    useEffect(() => {
        const fetchData = async () => {
            const response = await axios.get<Agent>(`${window.URLS.agentsResource}/${formik.values.params.agentId}`);
            const agent = response.data;

            setAgentName(agent.name || `Agent #${formik.values.params.agentId}`);
        };

        if (formik.values.params.agentId && !agentName) {
            fetchData();
        }
    }, [formik.values.params.agentId, agentName]);

    const setSelectValue = (option: any) => {
        if (option) {
            formik.setFieldValue('params.agentId', option.value);
            setAgentName(option.label);
        }
    };

    if (agentRestricted) {
        return (
            <>
                <Form.Group className="mb-3" controlId="params.agentId">
                    <Form.Label>{i18n.t('jt_select_the_agent_who_will_be_assigned.text')}</Form.Label>
                    <RestrictedFieldInput />
                </Form.Group>
                <ActionAssignBaseFormFields formik={formik} setTimeoutValidity={setTimeoutValidity} />
            </>
        );
    }

    return (
        <>
            <Form.Group className="mb-3" controlId="params.agentId" >
                <Form.Label>{i18n.t('jt_select_the_agent_who_will_be_assigned.text')}</Form.Label>
                <AsyncSelect<AgentOption>
                    isClearable={false}
                    cacheOptions
                    value={{
                        label: agentName,
                        value: formik.values.params.agentId,
                    }}
                    defaultOptions
                    loadOptions={getAgentOptions}
                    hideSelectedOptions={false}
                    onChange={setSelectValue}
                    onBlur={formik.handleBlur}
                    styles={{
                        menu: (provided) => ({
                            ...provided,
                            zIndex: 10,
                        }),
                        control: (provided) => {
                            if (formik.touched.params?.agentId && formik.errors.params?.agentId) {
                                return {
                                    ...provided,
                                    border: '1px solid #dc3545',
                                };
                            }

                            return provided;
                        },
                    }}
                    aria-label="select-agent-to-assign"
                />
                <Form.Control.Feedback
                    type="invalid"
                    style={{
                        display: formik.touched.params?.agentId && formik.errors.params?.agentId ? 'block' : 'none',
                    }}
                >
                    {formik.errors.params?.agentId}
                </Form.Control.Feedback>
            </Form.Group >

            <ActionAssignBaseFormFields formik={formik} setTimeoutValidity={setTimeoutValidity} />
        </>
    );
});

ActionAssignAgentFormFields.displayName = 'ActionAssignAgentFormFields';

export default ActionAssignAgentFormFields; 